/**
 * Serviço de Planejamentos de Aula
 *
 * Gerencia operações de upload, listagem, aprovação e download de planejamentos de aula.
 * Usa o Auth Service (Python/FastAPI) para todas as operações.
 */

import { apiServiceClient, getAuthToken, getTenantSubdomain } from "../config/api";

/**
 * Serviço de planejamentos
 */
export const planejamentosService = {
  /**
   * Upload de planejamento de aula
   * @param {File} file - Arquivo PDF ou DOCX
   * @param {string} nucleoId - ID do núcleo
   * @param {string} turmaId - ID da turma
   * @param {number} mes - Mês (1-12)
   * @param {number} ano - Ano
   * @returns {Promise<Object>}
   */
  async uploadPlanejamento(file, nucleoId, turmaId, mes, ano) {
    // Validar parâmetros
    if (!file) {
      throw new Error("Arquivo é obrigatório");
    }
    if (!nucleoId) {
      throw new Error("Núcleo é obrigatório");
    }
    if (!turmaId) {
      throw new Error("Turma é obrigatória");
    }
    if (!mes || mes < 1 || mes > 12) {
      throw new Error("Mês inválido");
    }
    if (!ano || ano < 2020 || ano > 2100) {
      throw new Error("Ano inválido");
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("nucleo_id", String(nucleoId));
    formData.append("turma_id", String(turmaId));
    formData.append("mes", String(mes));
    formData.append("ano", String(ano));





    try {
      // apiServiceClient.post já trata FormData corretamente (remove Content-Type)
      const result = await apiServiceClient.post("/api/planejamentos", formData);
      return result;
    } catch (error) {
      console.error("[PlanejamentosService] Erro no upload:", error);
      // Se for 404, pode ser que o módulo não esteja disponível no backend
      if (error.status === 404) {
        throw new Error(
          "Endpoint de planejamentos não encontrado. Verifique se o módulo de planejamentos está habilitado no backend."
        );
      }
      throw error;
    }
  },

  /**
   * Lista planejamentos do monitor atual
   * @param {Object} filters - Filtros opcionais
   * @param {string} filters.status - Status (EM_ANALISE, APROVADO, NEGADO)
   * @param {number} filters.mes - Mês (1-12)
   * @param {number} filters.ano - Ano
   * @param {string} filters.turmaId - ID da turma
   * @returns {Promise<Array>}
   */
  async getMeusPlanejamentos(filters = {}) {
    const params = new URLSearchParams();

    if (filters.status) params.append("status_filter", filters.status);
    if (filters.mes) params.append("mes", filters.mes.toString());
    if (filters.ano) params.append("ano", filters.ano.toString());
    if (filters.turmaId) params.append("turma_id", filters.turmaId);

    const queryString = params.toString();
    const url = `/api/planejamentos/meus${queryString ? `?${queryString}` : ""}`;

    return apiServiceClient.get(url);
  },

  /**
   * Lista planejamentos do núcleo (para gestores)
   * @param {Object} filters - Filtros opcionais
   * @param {string} filters.nucleoId - ID do núcleo
   * @param {string} filters.status - Status (EM_ANALISE, APROVADO, NEGADO)
   * @param {number} filters.mes - Mês (1-12)
   * @param {number} filters.ano - Ano
   * @param {string} filters.turmaId - ID da turma
   * @returns {Promise<Array>}
   */
  async getPlanejamentosNucleo(filters = {}) {
    const params = new URLSearchParams();

    if (filters.nucleoId) params.append("nucleo_id", filters.nucleoId);
    if (filters.status) params.append("status_filter", filters.status);
    if (filters.mes) params.append("mes", filters.mes.toString());
    if (filters.ano) params.append("ano", filters.ano.toString());
    if (filters.turmaId) params.append("turma_id", filters.turmaId);

    const queryString = params.toString();
    const url = `/api/planejamentos${queryString ? `?${queryString}` : ""}`;

    return apiServiceClient.get(url);
  },

  /**
   * Busca detalhes de um planejamento por ID
   * @param {string} id - ID do planejamento
   * @returns {Promise<Object>}
   */
  async getPlanejamentoById(id) {
    return apiServiceClient.get(`/api/planejamentos/${id}`);
  },

  /**
   * Download do arquivo de planejamento
   * @param {string} id - ID do planejamento
   * @returns {Promise<Blob>}
   */
  async downloadPlanejamento(id) {
    // Download de blob: fetch direto (apiServiceClient espera JSON)
    const { apiConfig } = await import("../config/api");
    const baseURL = apiConfig.apiService.baseURL;
    const url = `${baseURL}/api/planejamentos/${id}/download`;

    if (!id) {
      throw new Error("ID do planejamento é obrigatório");
    }

    const token = getAuthToken();
    if (!token) {
      throw new Error("Sessão expirada. Faça login novamente.");
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };
    const subdomain = getTenantSubdomain();
    if (subdomain) {
      headers["X-Tenant-Subdomain"] = subdomain;
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
      credentials: "same-origin",
    });

    if (!response.ok) {
      // Tentar obter mensagem de erro do backend
      let errorMessage = "Erro ao fazer download";
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch (e) {
        // Se não conseguir parsear JSON, usar status text
        errorMessage = response.statusText || errorMessage;
      }

      // Mensagens mais específicas baseadas no status (mantém detail do backend quando existir)
      if (response.status === 404) {
        errorMessage = errorMessage === "Erro ao fazer download"
          ? "Planejamento não encontrado"
          : errorMessage;
      } else if (response.status === 403) {
        errorMessage = errorMessage === "Erro ao fazer download"
          ? "Você não tem permissão para baixar este planejamento"
          : errorMessage;
      }

      throw new Error(errorMessage);
    }

    // Verificar se a resposta tem conteúdo
    const contentLength = response.headers.get("content-length");

    // Nota: Content-Length pode não estar presente em StreamingResponse do FastAPI
    // Vamos verificar o blob.size depois, que é mais confiável
    if (contentLength && contentLength === "0") {
      console.warn("Content-Length indica arquivo vazio, mas vamos verificar o blob mesmo assim");
    }

    const blob = await response.blob();

    // Verificar se o blob foi criado corretamente
    if (!blob) {
      throw new Error("Erro ao criar blob do arquivo");
    }

    if (blob.size === 0) {
      throw new Error("Arquivo vazio ou não encontrado");
    }



    // Extrair nome do arquivo do header Content-Disposition se disponível
    const contentDisposition = response.headers.get("content-disposition");
    let filename = null;
    if (contentDisposition) {
      // Tentar diferentes formatos de Content-Disposition
      // Formato 1: filename="nome_arquivo"
      // Formato 2: filename*=UTF-8''nome_arquivo
      // Formato 3: filename=nome_arquivo
      let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);

      if (!filenameMatch) {
        // Tentar formato sem aspas
        filenameMatch = contentDisposition.match(/filename[^;=\n]*=([^;\n]+)/i);
      }

      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].trim().replace(/^['"]|['"]$/g, "");

        // Se começar com UTF-8'', remover o prefixo
        if (filename.startsWith("UTF-8''")) {
          filename = filename.substring(7);
        }

        // Decodificar se estiver em formato URL
        try {
          filename = decodeURIComponent(filename);
        } catch (e) {
          // Se falhar, usar o nome original
          console.warn("Não foi possível decodificar o nome do arquivo:", e);
        }
      }
    }

    return { blob, filename };
  },

  /**
   * Aprova um planejamento
   * @param {string} id - ID do planejamento
   * @param {string} observacoes - Observações opcionais
   * @returns {Promise<Object>}
   */
  async aprovarPlanejamento(id, observacoes = null) {
    return apiServiceClient.patch(`/api/planejamentos/${id}/aprovar`, {
      observacoes: observacoes || null,
    });
  },

  /**
   * Nega um planejamento
   * @param {string} id - ID do planejamento
   * @param {string} observacoes - Observações obrigatórias
   * @returns {Promise<Object>}
   */
  async negarPlanejamento(id, observacoes) {
    return apiServiceClient.patch(`/api/planejamentos/${id}/negar`, {
      observacoes: observacoes,
    });
  },

  /**
   * Deleta um planejamento
   * @param {string} id - ID do planejamento
   * @returns {Promise<Object>}
   */
  async deletePlanejamento(id) {
    return apiServiceClient.delete(`/api/planejamentos/${id}`);
  },
};

export default planejamentosService;

